import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Copy, Eye, EyeOff, ChevronUp, ChevronDown, Download, ExternalLink, Save, Upload, Image, BarChart3, X } from "lucide-react";
import { api, API, BACKEND_URL } from "../lib/api";

const STEP_TYPES = [
  ["welcome", "Приветствие"], ["identify", "Идентификация"], ["form", "Анкета (поля)"],
  ["question", "Вопрос (опрос)"], ["advertisement", "Реклама"], ["custom_text", "Свой текст"], ["review", "Отзыв (платформы)"],
];
const FIELD_TYPES = [
  ["text", "Текст"], ["number", "Число"], ["email", "Email"], ["phone", "Телефон"], ["date", "Дата"],
  ["textarea", "Длинный текст"], ["select", "Выпадающий список"], ["radio", "Один вариант"], ["checkbox", "Несколько вариантов"],
];
const QTYPES = [
  ["rating", "Оценка (звёзды)"], ["radio", "Один вариант"], ["checkbox", "Несколько вариантов"], ["select", "Выпадающий список"],
  ["text", "Текст"], ["textarea", "Длинный текст"], ["number", "Число"], ["date", "Дата"],
];
const TYPE_LABELS = { wifi: "Wi-Fi доступ", promo: "Промокод", survey: "Опрос", lead: "Сбор контактов", loyalty: "Лояльность", review: "Отзыв → подарок", custom: "Свой сценарий" };
const LANGS = ["ru", "uz", "en"];

const I18nInput = ({ value = {}, onChange, placeholder, textarea }) => (
  <div className="grid md:grid-cols-3 gap-2">
    {LANGS.map((l) => {
      const El = textarea ? "textarea" : "input";
      return <El key={l} className="admin-input" placeholder={`${placeholder} (${l.toUpperCase()})`} value={value?.[l] || ""}
        onChange={(e) => onChange({ ...value, [l]: e.target.value })} />;
    })}
  </div>
);

const STD_FIELD_KEYS = [
  ["first_name", "Имя"], ["last_name", "Фамилия"], ["phone", "Телефон"], ["email", "Email"],
  ["birthday", "Дата рождения"], ["gender", "Пол"], ["city", "Город"],
];

function FieldEditor({ fields = [], onChange }) {
  const upd = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, d) => {
    const arr = [...fields];
    const j = i + d;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const stdKeys = STD_FIELD_KEYS.map((k) => k[0]);
  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={i} className="bg-gray-50 rounded-xl p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <select className="admin-input !w-40" title="Куда сохраняется ответ в профиле клиента" value={stdKeys.includes(f.key) ? f.key : "__custom"}
              onChange={(e) => upd(i, { key: e.target.value === "__custom" ? (stdKeys.includes(f.key) ? `field_${i + 1}` : f.key) : e.target.value })}>
              {STD_FIELD_KEYS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              <option value="__custom">Своё поле</option>
            </select>
            <select className="admin-input !w-40" value={f.type} onChange={(e) => upd(i, { type: e.target.value })}>
              {FIELD_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={!!f.required} onChange={(e) => upd(i, { required: e.target.checked })} /> Обязат. <span className="text-[#FF0000]">*</span></label>
            <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={f.visible !== false} onChange={(e) => upd(i, { visible: e.target.checked })} /> Показ.</label>
            <div className="ml-auto flex gap-1">
              <button onClick={() => move(i, -1)} className="p-1 bg-white rounded"><ChevronUp size={13} /></button>
              <button onClick={() => move(i, 1)} className="p-1 bg-white rounded"><ChevronDown size={13} /></button>
              <button onClick={() => onChange(fields.filter((_, j) => j !== i))} className="p-1 bg-white rounded text-red-500"><Trash2 size={13} /></button>
            </div>
          </div>
          <I18nInput value={f.label} onChange={(v) => upd(i, { label: v })} placeholder="Подпись поля" />
          {["select", "radio", "checkbox"].includes(f.type) && (
            <OptionsEditor options={f.options || []} onChange={(o) => upd(i, { options: o })} />
          )}
        </div>
      ))}
      <button className="admin-btn admin-btn-ghost text-xs" onClick={() => onChange([...fields, { key: `field_${fields.length + 1}`, type: "text", label: {}, required: false, visible: true }])}>
        <Plus size={13} /> Добавить поле
      </button>
    </div>
  );
}

function OptionsEditor({ options, onChange }) {
  return (
    <div className="space-y-1.5 pl-2 border-l-2 border-yellow-200">
      <div className="text-[11px] font-bold text-gray-400">Варианты ответа</div>
      {options.map((o, i) => (
        <div key={i} className="flex gap-2 items-center">
          <span className="text-[11px] font-black text-gray-300 w-4 shrink-0 text-right">{i + 1}.</span>
          {LANGS.map((l) => (
            <input key={l} className={`admin-input !h-8 text-xs ${l === "ru" ? "" : "!w-28"}`} placeholder={l === "ru" ? "Вариант ответа" : l.toUpperCase()} value={o.label?.[l] || ""}
              onChange={(e) => onChange(options.map((x, j) => (j === i ? { ...x, label: { ...x.label, [l]: e.target.value } } : x)))} />
          ))}
          <button onClick={() => onChange(options.filter((_, j) => j !== i))} className="text-red-400"><Trash2 size={13} /></button>
        </div>
      ))}
      <button className="text-xs font-bold text-gray-500 underline" onClick={() => onChange([...options, { value: `opt_${Math.random().toString(36).slice(2, 7)}`, label: {} }])}>+ вариант</button>
    </div>
  );
}

function BannerSlot({ label, slot, value, ads, onSelect, onUpload, uploading }) {
  const fileRef = useRef(null);
  const sel = ads.find((a) => a.id === value);
  return (
    <div className="bg-gray-50 rounded-xl p-3 space-y-2" data-testid={`campaign-banner-slot-${slot}`}>
      <div className="text-xs font-extrabold text-gray-600">{label}</div>
      <select data-testid={`campaign-banner-select-${slot}`} className="admin-input" value={value || ""} onChange={(e) => onSelect(e.target.value || null)}>
        <option value="">Нет баннера</option>
        {ads.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      {sel?.image_url && <img src={sel.image_url} alt="" className="h-20 w-full rounded-lg object-cover" />}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
      <button data-testid={`campaign-banner-upload-${slot}`} type="button" className="admin-btn admin-btn-ghost text-xs w-full" disabled={uploading} onClick={() => fileRef.current?.click()}>
        <Upload size={13} /> {uploading ? "Загрузка..." : "Загрузить новое фото"}
      </button>
    </div>
  );
}

function ImageField({ value, onChange, placeholder = "Фото: вставьте ссылку или загрузите", testid }) {
  const fileRef = useRef(null);
  const [up, setUp] = useState(false);
  const uploadFile = async (file) => {
    if (!file) return;
    setUp(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${data.url}`);
      toast.success("Фото загружено. Не забудьте сохранить");
    } catch (e) { toast.error("Ошибка загрузки фото"); }
    setUp(false);
  };
  return (
    <div className="flex gap-2 items-center">
      {value && <img src={value} alt="" className="h-10 w-14 rounded-lg object-cover border border-gray-200 shrink-0" />}
      <input className="admin-input" placeholder={placeholder} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} />
      <button type="button" data-testid={testid} className="admin-btn admin-btn-ghost text-xs shrink-0" disabled={up} onClick={() => fileRef.current?.click()}>
        <Upload size={13} /> {up ? "Загрузка..." : "Загрузить фото"}
      </button>
    </div>
  );
}

// Simple survey results: per question — answer distribution with count and %
function SurveyResultsModal({ campaignId, onClose }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/admin/campaigns/${campaignId}/survey-results`).then((r) => setRes(r.data)).catch(() => setErr(true));
  }, [campaignId]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="survey-results-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-5 space-y-4 my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display font-black text-lg flex items-center gap-2"><BarChart3 size={18} className="text-[#FEBC10]" /> Результаты опроса</div>
            {res && <div className="text-xs font-bold text-gray-400">Всего прохождений: {res.total_completions}</div>}
          </div>
          <button data-testid="survey-results-close" onClick={onClose} className="p-2 bg-gray-100 rounded-xl"><X size={16} /></button>
        </div>
        {err && <div className="text-sm font-bold text-red-500">Не удалось загрузить результаты</div>}
        {!res && !err && <div className="py-8 text-center text-gray-400 font-bold">Загрузка...</div>}
        {res && !res.questions?.length && <div className="py-6 text-center text-gray-400 font-bold">В кампании нет вопросов</div>}
        {res?.questions?.map((q, qi) => (
          <div key={q.key} className="border border-gray-100 rounded-2xl p-4 space-y-2.5" data-testid={`survey-result-q-${qi}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-extrabold text-sm">{q.title?.ru || q.title?.en || `Вопрос ${qi + 1}`}</div>
              <div className="text-xs font-bold text-gray-400">Ответили: {q.answered}</div>
            </div>
            {q.qtype === "rating" && (
              <div className="text-sm font-black text-[#EFAF04]">Средняя оценка: {q.average} ★</div>
            )}
            {q.options && q.options.map((o) => (
              <div key={o.value}>
                <div className="flex justify-between text-xs font-bold mb-0.5">
                  <span>{o.label?.ru || o.label?.en || o.value}</span>
                  <span className="text-gray-500">{o.count} · {o.percent}%</span>
                </div>
                <div className="h-2.5 bg-gray-100 rounded-full">
                  <div className="h-2.5 rounded-full bg-[#FEBC10]" style={{ width: `${Math.min(100, o.percent)}%` }} />
                </div>
              </div>
            ))}
            {q.answers && (
              q.answers.length
                ? <div className="space-y-1 max-h-44 overflow-y-auto">{q.answers.map((a, i) => <div key={i} className="text-xs font-semibold bg-gray-50 rounded-lg px-3 py-1.5">{a}</div>)}</div>
                : <div className="text-xs text-gray-400 font-semibold">Пока нет ответов</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CampaignBuilder() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [ads, setAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [openStep, setOpenStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const uploadBanner = async (slot, file) => {
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: ad } = await api.post("/admin/ads", {
        name: `Баннер: ${c?.name?.ru || c?.slug || "кампания"} (${slot === "banner_top_id" ? "сверху" : "снизу"})`,
        title: {}, description: {}, button_text: {}, link: "",
        image_url: `${BACKEND_URL}${up.url}`, dark: false, photo_only: true, active: true,
      });
      setAds((prev) => [...prev, ad]);
      setC((prev) => ({ ...prev, [slot]: ad.id }));
      toast.success("Баннер загружен. Не забудьте сохранить кампанию");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Ошибка загрузки");
    }
    setUploadingSlot(null);
  };

  useEffect(() => {
    api.get(`/admin/campaigns/${id}`).then((r) => setC(r.data));
    api.get("/admin/ads").then((r) => setAds(r.data));
    api.get(`/admin/campaigns/${id}/analytics`).then((r) => setAnalytics(r.data));
  }, [id]);
  if (!c) return <div className="p-10 text-center text-gray-400 font-bold">Загрузка...</div>;

  const set = (patch) => setC({ ...c, ...patch });
  const steps = [...(c.steps || [])].sort((a, b) => a.order - b.order);
  const setSteps = (arr) => set({ steps: arr.map((s, i) => ({ ...s, order: i })) });
  const updStep = (sid, patch) => setSteps(steps.map((s) => (s.id === sid ? { ...s, ...patch } : s)));
  const moveStep = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= steps.length) return;
    const arr = [...steps];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setSteps(arr);
  };
  const addStep = () => {
    const s = { id: crypto.randomUUID(), type: "custom_text", order: steps.length, title: {}, text: {}, visible: true, required: true, config: {} };
    setSteps([...steps, s]);
    setOpenStep(s.id);
  };
  const cloneStep = (s) => setSteps([...steps, { ...JSON.parse(JSON.stringify(s)), id: crypto.randomUUID(), order: steps.length }]);

  const save = async (publish) => {
    setBusy(true);
    try {
      const body = { ...c };
      // Auto-generate stable answer keys for question steps (hidden from the user)
      body.steps = (body.steps || []).map((s) => (s.type === "question" && !(s.config && s.config.key)
        ? { ...s, config: { ...(s.config || {}), key: `q_${String(s.id || "").replace(/-/g, "").slice(0, 8)}` } }
        : s));
      if (publish !== undefined) body.active = publish;
      const { data } = await api.put(`/admin/campaigns/${id}`, body);
      setC(data);
      toast.success(publish ? "Опубликовано! QR активен" : "Сохранено");
    } catch (e) { toast.error(e.response?.data?.detail === "slug_taken" ? "Slug занят" : "Ошибка сохранения"); }
    setBusy(false);
  };

  const url = `${window.location.origin}/c/${c.slug}`;

  return (
    <div className="space-y-4" data-testid="campaign-builder">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Link to="/admin/campaigns" className="p-2 bg-white rounded-xl border border-gray-200"><ArrowLeft size={16} /></Link>
          <h1 className="font-display font-black text-xl">{c.name?.ru || "Кампания"} <span className="text-xs font-bold text-gray-400 uppercase ml-1">{TYPE_LABELS[c.type] || c.type}</span></h1>
        </div>
        <div className="flex gap-2">
          {steps.some((s) => s.type === "question") && (
            <button data-testid="survey-results-btn" className="admin-btn admin-btn-ghost" onClick={() => setShowResults(true)}><BarChart3 size={15} /> Результаты опроса</button>
          )}
          <button data-testid="builder-save-btn" className="admin-btn admin-btn-ghost" disabled={busy} onClick={() => save()}><Save size={15} /> Сохранить</button>
          <button data-testid="builder-publish-btn" className="admin-btn" disabled={busy} onClick={() => save(true)}>Опубликовать</button>
        </div>
      </div>

      {showResults && <SurveyResultsModal campaignId={id} onClose={() => setShowResults(false)} />}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
            <div className="text-sm font-extrabold">Основное</div>
            <I18nInput value={c.name} onChange={(v) => set({ name: v })} placeholder="Название" />
            <I18nInput value={c.description} onChange={(v) => set({ description: v })} placeholder="Описание" />
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-1.5 text-sm"><span className="font-bold text-gray-500">Адрес (slug):</span>
                <input data-testid="builder-slug-input" className="admin-input !w-40" value={c.slug} onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} /></div>
              <div className="flex items-center gap-1.5 text-sm"><span className="font-bold text-gray-500">Идентификация:</span>
                <select data-testid="builder-identification-select" className="admin-input !w-44" value={c.identification?.method || "none"}
                  onChange={(e) => set({ identification: { ...c.identification, method: e.target.value } })}>
                  <option value="none">Отключена</option><option value="telegram">Телефон (Telegram)</option><option value="email">Email</option>
                </select></div>
              <label className="flex items-center gap-1.5 text-sm font-bold">
                <input data-testid="builder-active-toggle" type="checkbox" checked={!!c.active} onChange={(e) => set({ active: e.target.checked })} /> Активна
              </label>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3" data-testid="campaign-banners-section">
            <div className="flex items-center gap-2 text-sm font-extrabold"><Image size={15} /> Баннеры кампании</div>
            <p className="text-xs text-gray-500 font-semibold">Показываются сверху и снизу на всех этапах и на экране результата.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <BannerSlot label="Баннер сверху" slot="banner_top_id" value={c.banner_top_id} ads={ads}
                onSelect={(v) => set({ banner_top_id: v })} onUpload={(f) => uploadBanner("banner_top_id", f)} uploading={uploadingSlot === "banner_top_id"} />
              <BannerSlot label="Баннер снизу" slot="banner_bottom_id" value={c.banner_bottom_id} ads={ads}
                onSelect={(v) => set({ banner_bottom_id: v })} onUpload={(f) => uploadBanner("banner_bottom_id", f)} uploading={uploadingSlot === "banner_bottom_id"} />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-extrabold">Этапы сценария ({steps.length})</div>
              <button data-testid="builder-add-step-btn" className="admin-btn text-xs !h-8" onClick={addStep}><Plus size={13} /> Этап</button>
            </div>
            {steps.map((s, i) => (
              <div key={s.id} className={`border rounded-xl ${s.visible === false ? "opacity-50" : ""} ${openStep === s.id ? "border-[#FEBC10]" : "border-gray-150"}`} data-testid={`builder-step-${i}`}>
                <div className="flex items-center gap-2 p-3 cursor-pointer" onClick={() => setOpenStep(openStep === s.id ? null : s.id)}>
                  <span className="w-6 h-6 rounded-full bg-[#FEBC10] text-xs font-black flex items-center justify-center">{i + 1}</span>
                  <span className="font-bold text-sm flex-1">{s.title?.ru || STEP_TYPES.find((t) => t[0] === s.type)?.[1] || s.type} <span className="text-xs text-gray-400 ml-1">{STEP_TYPES.find((t) => t[0] === s.type)?.[1] || s.type}</span></span>
                  <button onClick={(e) => { e.stopPropagation(); moveStep(i, -1); }} className="p-1 bg-gray-100 rounded"><ChevronUp size={13} /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveStep(i, 1); }} className="p-1 bg-gray-100 rounded"><ChevronDown size={13} /></button>
                  <button onClick={(e) => { e.stopPropagation(); updStep(s.id, { visible: s.visible === false }); }} className="p-1 bg-gray-100 rounded">{s.visible === false ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                  <button onClick={(e) => { e.stopPropagation(); cloneStep(s); }} className="p-1 bg-gray-100 rounded"><Copy size={13} /></button>
                  <button data-testid={`builder-step-delete-${i}`} onClick={(e) => { e.stopPropagation(); setSteps(steps.filter((x) => x.id !== s.id)); }} className="p-1 bg-gray-100 rounded text-red-500"><Trash2 size={13} /></button>
                </div>
                {openStep === s.id && (
                  <div className="p-3 pt-0 space-y-2.5">
                    <div className="flex flex-wrap gap-2">
                      <select className="admin-input !w-44" value={s.type} onChange={(e) => updStep(s.id, { type: e.target.value })}>
                        {STEP_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                      {s.type === "question" && (
                        <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={s.required !== false} onChange={(e) => updStep(s.id, { required: e.target.checked })} /> Обязательный вопрос <span className="text-[#FF0000]">*</span></label>
                      )}
                      {["advertisement", "custom_text"].includes(s.type) && (
                        <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={s.required === false} onChange={(e) => updStep(s.id, { required: !e.target.checked })} /> Можно пропустить</label>
                      )}
                    </div>
                    <I18nInput value={s.title} onChange={(v) => updStep(s.id, { title: v })} placeholder="Заголовок" />
                    <I18nInput value={s.text} onChange={(v) => updStep(s.id, { text: v })} placeholder="Текст" />
                    {s.type === "advertisement" && (
                      <div className="bg-gray-50 rounded-xl p-3 space-y-2" data-testid={`step-ad-block-${i}`}>
                        <div className="text-xs font-extrabold text-gray-600">Баннер для этого этапа</div>
                        <select data-testid={`step-ad-select-${i}`} className="admin-input" value={s.banner_id || ""} onChange={(e) => updStep(s.id, { banner_id: e.target.value || null })}>
                          <option value="">— выберите баннер (из раздела «Реклама») —</option>
                          {ads.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        {ads.find((a) => a.id === s.banner_id)?.image_url && (
                          <img src={ads.find((a) => a.id === s.banner_id).image_url} alt="" className="h-20 w-full rounded-lg object-cover" />
                        )}
                      </div>
                    )}
                    {s.type === "form" && <FieldEditor fields={s.config?.fields || []} onChange={(f) => updStep(s.id, { config: { ...s.config, fields: f } })} />}
                    {s.type === "question" && (
                      <div className="space-y-2">
                        <select className="admin-input !w-52" value={s.config?.qtype || "radio"} onChange={(e) => updStep(s.id, { config: { ...s.config, qtype: e.target.value } })}>
                          {QTYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                        {["radio", "checkbox", "select"].includes(s.config?.qtype) && (
                          <OptionsEditor options={s.config?.options || []} onChange={(o) => updStep(s.id, { config: { ...s.config, options: o } })} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {c.type === "wifi" && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
              <div className="text-sm font-extrabold">Wi-Fi настройки</div>
              <div className="grid md:grid-cols-3 gap-2">
                <input data-testid="wifi-ssid-input" className="admin-input" placeholder="SSID" autoComplete="off" name="craft-wifi-ssid" value={c.wifi?.ssid || ""} onChange={(e) => set({ wifi: { ...c.wifi, ssid: e.target.value } })} />
                <input data-testid="wifi-password-input" className="admin-input" placeholder="Пароль" type="text" autoComplete="off" name="craft-wifi-pass" value={c.wifi?.password || ""} onChange={(e) => set({ wifi: { ...c.wifi, password: e.target.value } })} />
                <select data-testid="wifi-duration-select" className="admin-input" value={c.wifi?.duration_minutes || 30} onChange={(e) => set({ wifi: { ...c.wifi, duration_minutes: +e.target.value } })}>
                  {[2, 5, 10, 30, 60, 120, 1440].map((m) => <option key={m} value={m}>{m >= 60 ? `${m / 60} ч` : `${m} мин`}</option>)}
                </select>
              </div>
            </div>
          )}
          {c.type === "promo" && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
              <div className="text-sm font-extrabold">Промокод</div>
              <div className="grid md:grid-cols-3 gap-2">
                <input className="admin-input" placeholder="Код (CRAFT10)" value={c.promo?.code || ""} onChange={(e) => set({ promo: { ...c.promo, code: e.target.value.toUpperCase() } })} />
                <input className="admin-input" type="number" placeholder="Кол-во использований" value={c.promo?.max_uses || ""} onChange={(e) => set({ promo: { ...c.promo, max_uses: +e.target.value } })} />
                <input className="admin-input" type="date" value={c.promo?.expires || ""} onChange={(e) => set({ promo: { ...c.promo, expires: e.target.value } })} />
              </div>
              <I18nInput value={c.promo?.conditions} onChange={(v) => set({ promo: { ...c.promo, conditions: v } })} placeholder="Условия" />
            </div>
          )}
          {c.type === "loyalty" && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
              <div className="text-sm font-extrabold">Программа лояльности</div>
              <div className="grid md:grid-cols-3 gap-2">
                <input className="admin-input" type="number" placeholder="Кол-во посещений" value={c.loyalty?.required || 6} onChange={(e) => set({ loyalty: { ...c.loyalty, required: +e.target.value } })} />
                <select className="admin-input" value={c.loyalty?.count_type || "visit"} onChange={(e) => set({ loyalty: { ...c.loyalty, count_type: e.target.value } })}>
                  <option value="visit">Посещения</option><option value="purchase">Покупки</option>
                </select>
                <input className="admin-input" placeholder="Промокод награды" value={c.loyalty?.reward?.promo_code || ""} onChange={(e) => set({ loyalty: { ...c.loyalty, reward: { ...c.loyalty?.reward, promo_code: e.target.value } } })} />
              </div>
              <I18nInput value={c.loyalty?.reward?.name} onChange={(v) => set({ loyalty: { ...c.loyalty, reward: { ...c.loyalty?.reward, name: v } } })} placeholder="Название награды" />
              <I18nInput value={c.loyalty?.reward?.description} onChange={(v) => set({ loyalty: { ...c.loyalty, reward: { ...c.loyalty?.reward, description: v } } })} placeholder="Описание награды" />
              <ImageField testid="loyalty-reward-photo-upload" value={c.loyalty?.reward?.image_url} onChange={(v) => set({ loyalty: { ...c.loyalty, reward: { ...c.loyalty?.reward, image_url: v } } })} placeholder="Фото награды: ссылка или загрузите" />
            </div>
          )}
          {c.type === "survey" && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-extrabold">Награда за опрос</div>
                <label className="text-xs font-bold flex items-center gap-1"><input type="checkbox" checked={!!c.survey_reward?.enabled} onChange={(e) => set({ survey_reward: { ...c.survey_reward, enabled: e.target.checked } })} /> Включена</label>
              </div>
              <I18nInput value={c.survey_reward?.name} onChange={(v) => set({ survey_reward: { ...c.survey_reward, name: v } })} placeholder="Название подарка" />
              <input className="admin-input" placeholder="Промокод" value={c.survey_reward?.promo_code || ""} onChange={(e) => set({ survey_reward: { ...c.survey_reward, promo_code: e.target.value } })} />
              <ImageField testid="survey-reward-photo-upload" value={c.survey_reward?.image_url} onChange={(v) => set({ survey_reward: { ...c.survey_reward, image_url: v } })} placeholder="Фото подарка: ссылка или загрузите" />
            </div>
          )}

          {c.type === "review" && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
              <div className="text-sm font-extrabold">Программа «Отзыв — подарок»</div>
              <div className="text-xs text-gray-400 font-semibold">Платформы для отзыва (пользователь выбирает одну)</div>
              {(c.review?.platforms || []).map((p, i) => (
                <div key={p.key || i} className="flex gap-2">
                  <input className="admin-input !w-40" placeholder="Название" value={p.label?.ru || ""}
                    onChange={(e) => set({ review: { ...c.review, platforms: c.review.platforms.map((x, j) => (j === i ? { ...x, label: { ru: e.target.value, uz: x.label?.uz || e.target.value, en: x.label?.en || e.target.value } } : x)) } })} />
                  <input className="admin-input" placeholder="Ссылка на отзыв (https://...)" value={p.url || ""}
                    onChange={(e) => set({ review: { ...c.review, platforms: c.review.platforms.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) } })} />
                  <button className="text-red-400" onClick={() => set({ review: { ...c.review, platforms: c.review.platforms.filter((_, j) => j !== i) } })}><Trash2 size={14} /></button>
                </div>
              ))}
              <button className="admin-btn admin-btn-ghost text-xs" onClick={() => set({ review: { ...c.review, platforms: [...(c.review?.platforms || []), { key: `p${(c.review?.platforms || []).length + 1}`, label: {}, url: "" }] } })}><Plus size={13} /> Платформа</button>
              <div className="text-xs text-gray-400 font-semibold pt-1">Подарок</div>
              <I18nInput value={c.review?.reward?.name} onChange={(v) => set({ review: { ...c.review, reward: { ...c.review?.reward, name: v } } })} placeholder="Название подарка" />
              <input className="admin-input" placeholder="Промокод" value={c.review?.reward?.promo_code || ""} onChange={(e) => set({ review: { ...c.review, reward: { ...c.review?.reward, promo_code: e.target.value } } })} />
              <ImageField testid="review-reward-photo-upload" value={c.review?.reward?.image_url} onChange={(v) => set({ review: { ...c.review, reward: { ...c.review?.reward, image_url: v } } })} placeholder="Фото подарка: ссылка или загрузите" />
            </div>
          )}

          <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2">
            <div className="text-sm font-extrabold">Экран результата</div>
            <p className="text-xs text-gray-400 font-semibold">Баннеры кампании (сверху/снизу) показываются здесь автоматически.</p>
            <I18nInput value={c.success?.title} onChange={(v) => set({ success: { ...c.success, title: v } })} placeholder="Заголовок" />
            <I18nInput value={c.success?.text} onChange={(v) => set({ success: { ...c.success, text: v } })} placeholder="Текст" />
            <div className="flex flex-wrap gap-2">
              <input className="admin-input !w-56" placeholder="Ссылка кнопки (CTA)" value={c.success?.button_link || ""} onChange={(e) => set({ success: { ...c.success, button_link: e.target.value } })} />
            </div>
            <I18nInput value={c.success?.button_text} onChange={(v) => set({ success: { ...c.success, button_text: v } })} placeholder="Текст кнопки" />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-4 border border-gray-100 text-center space-y-3" data-testid="qr-preview">
            <div className="text-sm font-extrabold">QR-КОД</div>
            <img src={`${API}/qr/campaign/${c.slug}.png`} alt="QR" className="w-44 h-44 mx-auto rounded-xl border border-gray-100" />
            <div className="text-xs text-gray-500 break-all font-semibold">{url}</div>
            <div className="grid grid-cols-3 gap-2">
              {["png", "svg", "pdf"].map((f) => (
                <a key={f} data-testid={`qr-download-${f}`} href={`${API}/qr/campaign/${c.slug}.${f}`} target="_blank" rel="noreferrer" className="admin-btn admin-btn-ghost !h-8 text-xs uppercase"><Download size={12} /> {f}</a>
              ))}
            </div>
            <a data-testid="qr-open-link" href={url} target="_blank" rel="noreferrer" className="admin-btn w-full"><ExternalLink size={14} /> Открыть</a>
            <a data-testid="qr-flyer-link" href={`/flyer/${c.slug}`} target="_blank" rel="noreferrer" className="admin-btn admin-btn-red w-full"><Download size={14} /> Флаер для печати</a>
          </div>
          {analytics && (
            <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2" data-testid="campaign-funnel">
              <div className="text-sm font-extrabold">Воронка конверсии</div>
              {[["scanned", "Сканирований"], ["started", "Начали"], ["verified", "Подтвердили"], ["completed", "Завершили"], ["dropped", "Отвалились"]].map(([k, l]) => {
                const max = analytics.funnel.scanned || 1;
                return (
                  <div key={k}>
                    <div className="flex justify-between text-xs font-bold"><span>{l}</span><span>{analytics.funnel[k]}</span></div>
                    <div className="h-2 bg-gray-100 rounded-full"><div className={`h-2 rounded-full ${k === "dropped" ? "bg-[#FF0000]" : "bg-[#FEBC10]"}`} style={{ width: `${Math.min(100, (analytics.funnel[k] / max) * 100)}%` }} /></div>
                  </div>
                );
              })}
              <div className="text-xs font-bold text-gray-500 pt-1">Конверсия: {analytics.conversion}%</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
